import { useState, type CSSProperties } from "react";
import type { UserSummary } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { usePrefs } from "../../lib/prefs";

const sizes = {
  xs: "size-5 text-[11px]",
  sm: "size-7 text-xs",
  md: "size-9 text-xs",
  lg: "size-10 text-sm",
  xl: "size-16 text-lg",
  "2xl": "size-24 text-2xl",
} as const;
type Size = keyof typeof sizes;

export type PresenceStatus = "online" | "idle" | "dnd" | "offline";
const STATUS_LABEL: Record<PresenceStatus, string> = { online: "Online", idle: "Idle", dnd: "Do not disturb", offline: "Offline" };

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic neutral tone so placeholders are distinguishable without colour noise. */
function tone(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const tones = ["bg-surface-3", "bg-surface-2", "bg-line-strong"];
  return tones[Math.abs(h) % tones.length];
}

/** Motion is reduced by the account preference or, on "system", by the OS. */
export function useReducedMotion() {
  const { reduceMotion } = usePrefs();
  if (reduceMotion === "on") return true;
  if (reduceMotion === "off") return false;
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * A person's avatar. Animated avatars play only when animated avatars are on
 * and motion isn't reduced; otherwise the first-frame still is shown.
 * Optional decoration ring and effect (see styles/social.css), and a presence
 * indicator that uses shape as well as colour (`status`). `online` is kept
 * for older callers and maps to online/offline.
 */
export function Avatar({
  src,
  staticSrc,
  name,
  size = "md",
  className,
  online,
  status,
  decoration,
  effect,
  accent,
}: {
  src: string | null | undefined;
  /** Still frame for animated avatars. */
  staticSrc?: string | null;
  name: string;
  size?: Size;
  className?: string;
  online?: boolean;
  status?: PresenceStatus;
  decoration?: string | null;
  effect?: string | null;
  /** Decoration colour; defaults to the app accent. */
  accent?: string | null;
}) {
  const [failed, setFailed] = useState(false);
  const prefs = usePrefs();
  const reduced = useReducedMotion();
  const animate = prefs.animatedAvatars && !reduced;
  const url = staticSrc && !animate ? staticSrc : src;
  const shownStatus: PresenceStatus | undefined = status ?? (online === undefined ? undefined : online ? "online" : "offline");
  const big = size === "xl" || size === "2xl";
  const decorated = !!decoration || (!!effect && effect !== "none");
  return (
    <span
      className={cn("avatar-shell", className)}
      data-size={size}
      data-still={!prefs.particleEffects || reduced ? "true" : undefined}
      style={accent ? ({ "--deco": accent } as CSSProperties) : undefined}
    >
      {decorated && effect && effect !== "none" && <span aria-hidden className="avatar-effect" data-effect={effect} />}
      {url && !failed ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className={cn("relative rounded-full object-cover bg-surface-2", sizes[size].split(" ").filter((c) => c.startsWith("size-")))}
        />
      ) : (
        <span aria-hidden className={cn("relative inline-flex select-none items-center justify-center rounded-full font-semibold text-fg-2", sizes[size], tone(name))}>
          {initials(name)}
        </span>
      )}
      {decoration && <span aria-hidden className="avatar-deco" data-deco={decoration} />}
      {shownStatus && (
        <span role="img" aria-label={STATUS_LABEL[shownStatus]} title={STATUS_LABEL[shownStatus]} className={cn("presence-dot", big ? "size-4" : "size-3")} data-status={shownStatus} />
      )}
    </span>
  );
}

/** Avatar for a UserSummary, with its decoration, effect and still frame. */
export function UserAvatar({
  user,
  size = "md",
  className,
  status,
  plain,
}: {
  user: Pick<UserSummary, "avatarUrl" | "displayName"> & Partial<Pick<UserSummary, "avatarStaticUrl" | "decoration" | "avatarEffect">>;
  size?: Size;
  className?: string;
  status?: PresenceStatus;
  /** Leave out decorations (dense lists). */
  plain?: boolean;
}) {
  return (
    <Avatar
      src={user.avatarUrl}
      staticSrc={user.avatarStaticUrl}
      name={user.displayName}
      size={size}
      className={className}
      status={status}
      decoration={plain ? null : user.decoration}
      effect={plain ? null : user.avatarEffect}
    />
  );
}

const cSizes = {
  "2xs": "size-[18px] text-[11px] rounded-[5px]",
  xs: "size-6 text-xs rounded-md",
  sm: "size-8 text-xs rounded-[9px]",
  md: "size-10 text-sm rounded-[11px]",
  lg: "size-12 text-base rounded-[13px]",
  xl: "size-16 text-xl rounded-[18px]",
  "2xl": "size-20 text-2xl rounded-[22px]",
} as const;

/** Communities use rounded squares so they're never confused with people. */
export function CommunityIcon({ src, name, size = "md", className }: { src: string | null | undefined; name: string; size?: keyof typeof cSizes; className?: string }) {
  const [failed, setFailed] = useState(false);
  const cls = cn("shrink-0 inline-flex items-center justify-center overflow-hidden font-semibold", cSizes[size], className);
  return src && !failed ? (
    <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className={cn(cls, "object-cover bg-surface-2")} />
  ) : (
    <span aria-hidden className={cn(cls, "select-none bg-surface-2 text-fg-2")}>
      {/* Two letters don't fit legibly in the smallest icons. */}
      {size === "2xs" || size === "xs" ? initials(name).slice(0, 1) : initials(name)}
    </span>
  );
}
